import { useCallback, useEffect, useMemo, useState } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button, Drawer, DrawerPosition, Heading } from '@savgroup-front-common/core';

import { getDossierHistoryByFileId } from '../../data/dossierHistoryMock';
import type { DossierHistoryStep } from '../../types/dossierHistory.types';
import { formatDossierDate } from '../../utils/dossierFormat';
import {
  $DrawerBody,
  $DrawerHeader,
  $EmptyState,
  $Section,
  $SectionTitle,
  $ShowAllButton,
  $ShowAllButtonWrap,
  $StepContent,
  $StepLabel,
  $StepMeta,
  $Timeline,
  $TimelineDot,
  $TimelineItem,
} from './DossierHistorySection.styles';

export interface DossierHistorySectionProps {
  fileId: string;
}

const PREVIEW_STEPS_COUNT = 4;

const DRAWER_WIDTH_PERCENT = 0.92;
const DRAWER_WIDTH_MIN = 320;
const DRAWER_WIDTH_MAX = 480;

const getDrawerWidthPx = (): number => {
  if (typeof window === 'undefined') {
    return 360;
  }

  const width = Math.round(window.innerWidth * DRAWER_WIDTH_PERCENT);

  return Math.min(DRAWER_WIDTH_MAX, Math.max(DRAWER_WIDTH_MIN, width));
};

const sortStepsByMostRecent = (steps: DossierHistoryStep[]): DossierHistoryStep[] =>
  [...steps].sort((left, right) => right.date.localeCompare(left.date));

interface HistoryTimelineProps {
  steps: DossierHistoryStep[];
}

const HistoryTimeline = ({ steps }: HistoryTimelineProps) => (
  <$Timeline>
    {steps.map((step) => (
      <$TimelineItem key={step.id}>
        <$TimelineDot aria-hidden />
        <$StepContent>
          <$StepLabel>{step.label}</$StepLabel>
          <$StepMeta>
            <span>{formatDossierDate(step.date)}</span>
            <span>{step.actorLabel}</span>
          </$StepMeta>
        </$StepContent>
      </$TimelineItem>
    ))}
  </$Timeline>
);

const DossierHistorySection = ({ fileId }: DossierHistorySectionProps) => {
  const [isFullHistoryOpen, setIsFullHistoryOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(getDrawerWidthPx);

  const steps = useMemo(
    () => sortStepsByMostRecent(getDossierHistoryByFileId(fileId)),
    [fileId],
  );
  const previewSteps = useMemo(() => steps.slice(0, PREVIEW_STEPS_COUNT), [steps]);
  const hasMoreSteps = steps.length > PREVIEW_STEPS_COUNT;

  useEffect(() => {
    const updateWidth = () => setDrawerWidth(getDrawerWidthPx());

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    setIsFullHistoryOpen(false);
  }, [fileId]);

  const handleOpenFullHistory = useCallback(() => {
    setIsFullHistoryOpen(true);
  }, []);

  const handleCloseFullHistory = useCallback(() => {
    setIsFullHistoryOpen(false);
  }, []);

  if (steps.length === 0) {
    return (
      <$Section data-testid="dossier-history-section">
        <$SectionTitle>Historique</$SectionTitle>
        <$EmptyState>Aucune étape disponible pour ce dossier.</$EmptyState>
      </$Section>
    );
  }

  return (
    <$Section data-testid="dossier-history-section">
      <$SectionTitle>Historique</$SectionTitle>
      <HistoryTimeline steps={previewSteps} />
      {hasMoreSteps ? (
        <$ShowAllButtonWrap>
          <$ShowAllButton
            type="button"
            onClick={handleOpenFullHistory}
            data-testid="dossier-history-show-all"
          >
            Tout afficher
          </$ShowAllButton>
        </$ShowAllButtonWrap>
      ) : null}

      <Drawer
        isOpen={isFullHistoryOpen}
        onClose={handleCloseFullHistory}
        position={DrawerPosition.Right}
        width={drawerWidth}
        dataTestId="dossier-history-drawer"
      >
        <$DrawerHeader>
          <Heading level={2}>Historique</Heading>
          <Button
            type={BUTTON_TYPES.BUTTON}
            tertiary
            onClick={handleCloseFullHistory}
            ariaLabel={{ id: 'dossier.history.drawer.close', defaultMessage: 'Fermer' }}
          >
            ✕
          </Button>
        </$DrawerHeader>
        <$DrawerBody>
          <HistoryTimeline steps={steps} />
        </$DrawerBody>
      </Drawer>
    </$Section>
  );
};

export default DossierHistorySection;
